import { Component, ComponentInterface, Element, h, Host, Listen, Prop } from '@stencil/core';
import {
    CssClassMap,
    FlexContentOptions,
    FlexDirectionOptions,
    FlexItemOptions,
    FlexWrapOptions,
    StyleEventDetail
} from '../../interface';
import { flexGapSupported } from '../../utils/utils';
import { itemStyle } from '../../utils/helpers';

@Component({
    tag: 'rs-item',
    styleUrl: 'item.css',
    shadow: true
})
export class Item implements ComponentInterface {
    private itemStyles = new Map<string, CssClassMap>();
    private item: HTMLElement;

    @Element() el!: HTMLRsItemElement;

    /**
     * If `true`, the user cannot interact with the item.
     */
    @Prop() disabled = false;

    /**
     * Contains a URL or a URL fragment that the hyperlink points to.
     * If this property is set, an anchor tag will be rendered.
     */
    @Prop() href: string | undefined;

    /**
     * Specifies the relationship of the target object to the link object.
     * The value is a space-separated list of [link types](https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types).
     */
    @Prop() rel: string | undefined;

    /**
     * Specifies where to display the linked URL.
     * Only applies when an `href` is provided.
     * Special keywords: `"_blank"`, `"_self"`, `"_parent"`, `"_top"`.
     */
    @Prop() target: string | undefined;

    /**
     * The type of the button. Only used when an `onclick` or `button` property is present.
     */
    @Prop() type: 'submit' | 'reset' | 'button' = 'button';

    /**
     * Possible values are: outline, shadow and none.
     * If outline, it adds border around the item
     */
    @Prop() appearance: 'outline' | 'shadow' | 'none' = 'none';

    /**
     * justify-content CSS rule
     * Will only work if we set 'flex' to true
     */
    @Prop() justifyContent: FlexContentOptions;

    /**
     * align-items CSS rule
     * Will only work if we set 'flex' to true
     */
    @Prop() alignItems: FlexItemOptions;

    /**
     * align-content CSS rule
     * Will only work if we set 'flex' to true
     */
    @Prop() alignContent: FlexContentOptions;

    /**
     * justify-items CSS rule
     * Will only work if we set 'flex' to true
     */
    @Prop() justifyItems: FlexItemOptions;

    /**
     * gap CSS rule
     * Note: This doesn't work on Safari
     */
    @Prop() gap: number = 0;

    /**
     * flex-wrap CSS rule
     */
    @Prop() flexWrap: FlexWrapOptions;

    /**
     * Useful when using Flex display. Sets the flex-direction css style
     */
    @Prop() flexDirection: FlexDirectionOptions;

    /**
     * If true, we apply default padding of 8px inside the item.
     */
    @Prop() padding: boolean = false;

    /**
     * If true, we display all the elements inside the item in a single row.
     */
    @Prop() flex: boolean = false;

    @Listen('rsItem')
    rsItem(ev: CustomEvent<StyleEventDetail>) {
        itemStyle(ev, this.itemStyles, this.el);
    }

    componentDidRender(): Promise<void> | void {
        if (!flexGapSupported()) {
            this.item.style.setProperty('--fgp-gap-row', `${this.gap.toString()}px`);
            this.item.style.setProperty('--fgp-gap-column', `${this.gap.toString()}px`);
            this.item.style.setProperty('--fgp-gap', `${this.gap.toString()}px`);
        }
    }

    // If the item has an href or button property it will render a native
    // anchor or button that is clickable
    private isClickable(): boolean {
        return (this.href !== undefined);
    }

    /**
     * This renders the component
     */
    render() {
        const { disabled, href, rel, target } = this;
        const childStyles = {};
        const clickable = this.isClickable();

        const TagType = clickable ? (href === undefined ? 'button' : 'a') : 'div' as any;
        const attrs = (TagType === 'button')
            ? { type: this.type }
            : {
                href,
                rel,
                target
            };
        this.itemStyles.forEach(value => {
            Object.assign(childStyles, value);
        });

        return (
            <Host
                class={{
                    ...childStyles,
                    'rs-item--padding': this.padding,
                    'rs-item--flex': this.flex
                }}
            >
                <TagType
                    {...attrs}
                    class={{
                        'item-native': true,
                        'no-flex-gap-support': !flexGapSupported()
                    }}
                    ref={el => this.item = el}
                    style={{
                        'flex-direction': this.flexDirection,
                        'align-items': this.alignItems,
                        'justify-content': this.justifyContent,
                        'align-content': this.alignContent,
                        'justify-items': this.justifyItems,
                        'gap': `${this.gap}px`,
                        'flex-wrap': this.flexWrap
                    }}
                    disabled={disabled}
                >
                    <slot name="start"/>
                    <slot/>
                    <slot name="end"/>
                </TagType>
            </Host>
        );
    }

}